/** HTTP Client Library. */
import axios from 'axios';

const awsAPI = process.env.REACT_APP_DEFAULT_API;
const auth0API = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;
const slackWebhookUrl = process.env.REACT_APP_ERROR_DETECT_API;
const currentEnvironment = process.env.REACT_APP_NODE_ENV; // If currentEnvironment is not 'production', assume it's a test environment.

const awsAPIOP = process.env.REACT_APP_DEFAULT_API_OP;

/** Default API call function.  */
export const callAPI = async ({
    method = 'get',
    module = null,
    component = null,
    type = null,
    getAccessTokenSilently,
    email,
    obj = null,
    content_type = 'application/json',
    onUploadProgress = null,
    responseType = 'json',
    onInit = () => {},
    onSuccess = () => {},
    onError = () => {},
    onFinal = () => {},
}) => {
    try {
        onInit();
        // Method to retrieve an access token using Auth0, makes an authenticated API call to fetch shipment-related data,
        // and handles errors by throwing them for further processing.
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: auth0API, // Include the audience of authorization parameters to authenticate API call.
            },
        });

        const apiConfig = {
            method: method,
            url: awsAPI,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': content_type,
            },
            params: {
                module: module,
                component: component,
                type: type,
                email: email,
                test: currentEnvironment === 'production' ? false : true,
            },
            data: obj instanceof FormData ? obj : { data: obj },
            onUploadProgress: onUploadProgress,
            responseType: responseType,
        };

        const resp = await axios(apiConfig);
        const error = isMissException(resp);

        if (error) {
            reportErrorToSlack({
                error: error,
                method: method,
                module: module,
                component: component,
                type: type,
                email: email,
                obj: obj,
                content_type: content_type,
                responseType: responseType,
                service_name: 'Management System',
            });
            return;
        }
        if (resp.status === 200) {
            onSuccess(resp);
        }
    } catch (error) {
        onError(error);
        reportErrorToSlack({
            error,
            method,
            module,
            component,
            type,
            email,
            obj,
            content_type,
            responseType,
            service_name: 'Management System',
        });
        console.error('API error', error);
    } finally {
        onFinal();
    }
};

export const callAPIOP = async ({
    method = 'get',
    module = null,
    component = null,
    type = null,
    getAccessTokenSilently,
    email,
    obj = null,
    content_type = 'application/json',
    onUploadProgress = null,
    responseType = 'json',
    onInit = () => {},
    onSuccess = () => {},
    onError = () => {},
    onFinal = () => {},
}) => {
    try {
        onInit();
        // Method to retrieve an access token using Auth0, makes an authenticated API call to fetch shipment-related data,
        // and handles errors by throwing them for further processing.
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: auth0API, // Include the audience of authorization parameters to authenticate API call.
            },
        });
        const apiConfig = {
            method: method,
            url: awsAPIOP,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': content_type,
            },
            params: {
                module: module,
                component: component,
                type: type,
                email: email,
                test: currentEnvironment === 'production' ? false : true,
            },
            data: obj instanceof FormData ? obj : { data: obj },
            onUploadProgress: onUploadProgress,
            responseType: responseType,
        };
        const resp = await axios(apiConfig);
        const error = isMissException(resp);

        if (error) {
            reportErrorToSlack({
                error: error,
                method: method,
                module: module,
                component: component,
                type: type,
                email: email,
                obj: obj,
                content_type: content_type,
                responseType: responseType,
                service_name: 'Management System',
            });
            return;
        }
        if (resp.status === 200) {
            onSuccess(resp);
        }
    } catch (error) {
        onError(error);
        reportErrorToSlack({
            error,
            method,
            module,
            component,
            type,
            email,
            obj,
            content_type,
            responseType,
            service_name: 'Management System',
        });
        console.error('API error', error);
    } finally {
        onFinal();
    }
};

/** Handler to detect api error case. */
export const isMissException = (response) => {
    let body = response?.body || response.data?.body || response.data?.['body-json']?.body;

    if (body && 'error' in body) return body;
    else return false;
};

function getCurrentTime() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/** Handler to send error info to slack. */
const reportErrorToSlack = async (props) => {
    const { error, method, module, component, type, email, obj, content_type, responseType, service_name } = props;
    const errorMessage = error?.response?.data?.message || error?.message || String(error);

    // Disable this code when deploying to production.
    // Only errors occurring in the production environment should be sent to Slack.
    // if (currentEnvironment !== 'production') return; //

    /** Structure of error report message. */
    const payload = {
        blocks: [
            {
                type: 'header',
                text: {
                    type: 'plain_text',
                    text: `⚠️ ${email} has created an error. Please check the error.`,
                    emoji: true,
                },
            },
            {
                type: 'section',
                fields: [
                    {
                        type: 'mrkdwn',
                        text: `*Error Message:*\n \`\`\`${errorMessage}\`\`\``,
                    },
                    {
                        type: 'mrkdwn',
                        text: `*When*\n ${getCurrentTime()}`,
                    },
                ],
            },
            {
                type: 'section',
                fields: [
                    {
                        type: 'mrkdwn',
                        text: `*Method:*\n ${method.toUpperCase()}`,
                    },
                    {
                        type: 'mrkdwn',
                        text: `*Module:*\n ${module}`,
                    },
                ],
            },
            {
                type: 'section',
                fields: [
                    {
                        type: 'mrkdwn',
                        text: `*Component:*\n ${component}`,
                    },
                    {
                        type: 'mrkdwn',
                        text: `*Type:*\n ${type}`,
                    },
                ],
            },
            {
                type: 'section',
                fields: [
                    {
                        type: 'mrkdwn',
                        text: `*Content Type:*\n ${content_type}`,
                    },
                    {
                        type: 'mrkdwn',
                        text: `*Response Type:*\n ${responseType}`,
                    },
                ],
            },
            {
                type: 'section',
                fields: [
                    {
                        type: 'mrkdwn',
                        text: `*Service Name:*\n ${service_name}`,
                    },
                    {
                        type: 'mrkdwn',
                        text: `*Object:*\n \`\`\`${JSON.stringify(obj)}\`\`\``,
                    },
                ],
            },
        ],
    };

    try {
        const resp = await axios.post(
            slackWebhookUrl,
            { payload },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: false,
                mode: 'cors',
            },
        );
    } catch (error) {
        console.error('Slack Webhook Error: ', error);
    }
};

/** Send ui rendering error to slack.  */
export const sendComponentErrorToSlack = async (error) => {
    // Disable this code when deploying to production.
    // Only errors occurring in the production environment should be sent to Slack.
    // if (currentEnvironment !== 'production') return;

    const payload = {
        text: `Unexpected error has occured in render component.`,
        attachments: [
            {
                pretext: `${error}`,
            },
        ],
    };

    try {
        const resp = await axios.post(
            slackWebhookUrl,
            { payload },
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: false,
                mode: 'cors',
            },
        );
    } catch (error) {
        console.error('Slack Webhook Error: ', error);
    }
};
